import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllStockBatchReportData,
  getStockBatchReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { expiryStatusList } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./StockBatch.css";

const tableHdrs = [
  "productName",
  "batchNumber",
  "location",
  "quantity",
  "expiryDate",
  "daysUntilExpiry",
  "status",
  "tradeName",
  "category",
  "hsnCode",
  "barcodeNo",
  "subunits",
  "mfgDate",
  "unitSellingPrice",
  "landedCost",
];
const initSearch = {
  productName: "",
  batchNo: "",
  location: [],
  category: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Quantity",
  "Expiry Date",
  "Days Until Expiry",
  "Status",
  "Trade Name",
  "HSN Code",
  "Barcode Number",
  "Subunit Qty",
  "Mfg. Date",
  "Unit Selling Price",
  "Landed Cost",
];
const dropFields = ["Status"];

const StockBatchReport = () => {
  const dispatch = useDispatch();
  const [selectedBatchNo, setSelectedBatchNo] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { stockBatch, stockBatchAll } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllStockBatchReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    selectedBatchNo,
    selectedLocation,
    selectedCategory,
    searchValues?.productName,
    searchValues?.batchNo,
    searchValues?.location,
    searchValues?.category,
  ]);

  const overviewData = stockBatch?.overview || stockBatch?.data || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "Batches tracked",
      value:
        cards?.batchesTracked?.display ??
        (cards?.batchesTracked !== undefined
          ? `${Number(cards.batchesTracked).toLocaleString()}`
          : cards?.batchesTracked?.value !== undefined
          ? `${Number(cards.batchesTracked.value).toLocaleString()}`
          : "4,500"),
    },
    {
      title: "Near expiry (30d)",
      value:
        cards?.nearExpiry30d?.display ??
        (cards?.nearExpiry30d !== undefined
          ? `${Number(cards.nearExpiry30d).toLocaleString()}`
          : cards?.nearExpiry30d?.value !== undefined
          ? `${Number(cards.nearExpiry30d.value).toLocaleString()}`
          : "0"),
    },
  ];

  // Expiry risk (Bar Chart)
  const rawExpiryRisk = Array.isArray(overviewData?.expiryRisk)
    ? overviewData.expiryRisk
    : [];

  const expiryRiskCategories =
    rawExpiryRisk.length > 0
      ? rawExpiryRisk.map((item) => item?.bucket || "<30 days")
      : ["<30 days", "31-60 days", "61-90 days", ">90 days"];

  const expiryRiskValues =
    rawExpiryRisk.length > 0
      ? rawExpiryRisk.map((item) =>
          typeof item?.value === "number" && !isNaN(item.value)
            ? item.value
            : typeof item?.units === "number" && !isNaN(item.units)
            ? item.units
            : typeof item?.batchCount === "number" && !isNaN(item.batchCount)
            ? item.batchCount
            : 0
        )
      : [0, 0, 0, 2900000];

  const expiryRiskChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "30%", borderRadius: 3 } },
    colors: ["#14B8A6"],
    xaxis: {
      categories: expiryRiskCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return `${val}`;
        },
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const expiryRiskChartSeries = [
    { name: "Value / Units", data: expiryRiskValues },
  ];

  const handleExportExcel = () => {
    const tableData = stockBatch?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Product Name": row?.productName ?? "",
      "Batch Number": row?.batchNumber ?? "",
      Location: row?.location ?? "",
      Quantity: row?.quantity ?? 0,
      "Expiry Date": row?.expiryDate ? moment(new Date(row?.expiryDate)).format("DD MMM YYYY") : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Batch Analysis");
    XLSX.writeFile(workbook, `Batch_Analysis_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { type: "batch", page: currentPage, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (selectedBatchNo) reqObj.batchNo = selectedBatchNo;
    if (selectedLocation !== "all") reqObj.locationId = selectedLocation;
    if (selectedCategory !== "all") reqObj.category = selectedCategory;
    if (searchValues?.productName?.length > 0) reqObj.productName = [searchValues?.productName];
    if (searchValues?.batchNo?.length > 0) reqObj.batchNo = [searchValues?.batchNo];
    if (searchValues?.location?.length > 0) reqObj.location = searchValues?.location?.map((li) => li?.value);
    if (searchValues?.category?.length > 0) reqObj.category = searchValues?.category?.map((li) => li?.value);
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    setPage(pge);
    dispatch(getStockBatchReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((stockBatch?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (stockBatch?.data ?? [])?.map((li) => ({
      ...li,
      productName: li?.productName ?? "",
      batchNumber: li?.batchNumber ?? "",
      location: li?.location ?? "",
      quantity: li?.quantity ?? 0,
      expiryDate: li?.expiryDate ? moment(new Date(li?.expiryDate)).format("DD MMM YYYY") : "",
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getProductNameList = () => uniq((stockBatch?.data ?? [])?.map((li) => li?.productName)?.filter(Boolean));
  const getBatchNoList = () => uniq((stockBatch?.data ?? [])?.map((li) => li?.batchNumber)?.filter(Boolean));

  const getDropListByType = (typ) => {
    return typ === "Status" ? expiryStatusList : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="stock-batch-container">
      {/* Title */}
      <h2 className="stock-batch-title">Batch analysis</h2>

      {/* Top Filter Bar */}
      <div className="stock-batch-filter-bar">
        <div style={{ width: 180 }}>
          <CustomTextField
            placeholder="Batch number"
            value={selectedBatchNo}
            handleChange={(e) => setSelectedBatchNo(e.target.value)}
            fullWidth
          />
        </div>

        <Select
          size="small"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All locations</MenuItem>
          <MenuItem value="clinic">Clinic</MenuItem>
          <MenuItem value="pharmacy">Pharmacy</MenuItem>
        </Select>

        <Select
          size="small"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All categories</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="stock-batch-export-group">
        <button type="button" className="stock-batch-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="stock-batch-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (2 Cards) */}
      <div className="stock-batch-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="stock-batch-card" key={idx}>
            <h4 className="stock-batch-card-title">{card.title}</h4>
            <div className="stock-batch-card-value-container">
              <h2 className="stock-batch-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Bar Chart: Expiry risk */}
      <div className="stock-batch-chart-card">
        <div className="stock-batch-chart-header">
          Expiry risk
        </div>
        <Chart
          options={expiryRiskChartOptions}
          series={expiryRiskChartSeries}
          type="bar"
          height={300}
        />
      </div>

      {/* Drill-down Section */}
      <div className="stock-batch-drilldown-header">Drill-down — batches</div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getProductNameList()}
              value={searchValues?.productName ?? []}
              name="productName"
              handleChange={onSearchChange}
              label="Product Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getBatchNoList()}
              value={searchValues?.batchNo ?? []}
              name="batchNo"
              handleChange={onSearchChange}
              label="Batch Number"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {stockBatch?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((stockBatch?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default StockBatchReport;
